import { useState } from "react";
import { Button, Chip, IconButton, Paper, TextField, Tooltip, Typography } from "@mui/material";
import { Add, ArrowBack, Delete } from "@mui/icons-material";
import { makeStyles } from "@/compat/muiStyles";
import type { RiskAssessmentAiAssessmentDTO, RiskAssessmentAiHazardDTO } from "../../types/riskAssessmentAiDtos";

const useStyles = makeStyles((theme) => ({
    intro: { color: theme.palette.text.secondary, marginBottom: theme.spacing(2) },
    details: { display: "flex", flexDirection: "column", gap: theme.spacing(2), marginBottom: theme.spacing(2), padding: theme.spacing(2) },
    hazard: { marginBottom: theme.spacing(2), padding: theme.spacing(2) },
    hazardHeader: { alignItems: "flex-start", display: "flex", gap: theme.spacing(1) },
    hazardFields: { display: "flex", flexDirection: "column", flex: 1, gap: theme.spacing(2) },
    listBlock: { marginTop: theme.spacing(2) },
    chips: { display: "flex", flexWrap: "wrap", gap: theme.spacing(1), marginTop: theme.spacing(1) },
    addRow: { display: "flex", gap: theme.spacing(1), marginTop: theme.spacing(1) },
    footer: { display: "flex", gap: theme.spacing(1), justifyContent: "space-between", marginTop: theme.spacing(2) },
    empty: { color: theme.palette.text.secondary, padding: theme.spacing(3), textAlign: "center" },
    scoresNote: { color: theme.palette.text.secondary, display: "block", marginTop: theme.spacing(1) },
}));

type HazardListKey = "controlsInPlace" | "additionalControls";

const listLabels: Record<HazardListKey, string> = {
    controlsInPlace: "Controls in place",
    additionalControls: "Additional controls required",
};

interface AiAssessmentReviewProps {
    assessment: RiskAssessmentAiAssessmentDTO;
    isSaving?: boolean;
    onChange: (assessment: RiskAssessmentAiAssessmentDTO) => void;
    onBack: () => void;
    onCreate: () => void;
}

export default function AiAssessmentReview({ assessment, isSaving, onChange, onBack, onCreate }: AiAssessmentReviewProps) {
    const classes = useStyles();
    const [drafts, setDrafts] = useState<Record<string, string>>({});

    const setHazards = (hazards: RiskAssessmentAiHazardDTO[]) => onChange({ ...assessment, hazards });

    const updateHazard = (index: number, changes: Partial<RiskAssessmentAiHazardDTO>) =>
        setHazards(assessment.hazards.map((hazard, i) => (i === index ? { ...hazard, ...changes } : hazard)));

    const removeItem = (index: number, key: HazardListKey, item: number) =>
        updateHazard(index, { [key]: assessment.hazards[index][key].filter((_, i) => i !== item) });

    const addPerson = () => {
        const value = (drafts.people ?? "").trim();
        if (!value) return;

        onChange({ ...assessment, peopleAtRisk: [...(assessment.peopleAtRisk ?? []), value] });
        setDrafts((prev) => ({ ...prev, people: "" }));
    };

    const addItem = (index: number, key: HazardListKey) => {
        const draftKey = `${index}-${key}`;
        const value = (drafts[draftKey] ?? "").trim();
        if (!value) return;

        updateHazard(index, { [key]: [...assessment.hazards[index][key], value] });
        setDrafts((prev) => ({ ...prev, [draftKey]: "" }));
    };

    return (
        <div>
            <Typography variant="body2" className={classes.intro}>
                Review what has been generated and amend anything that does not reflect your workplace. Nothing is
                created until you continue. Confirm the content with your competent person before relying on it, and
                remove anyone's name or personal details before you create the template.
            </Typography>

            <Paper className={classes.details}>
                <TextField
                    variant="outlined"
                    size="small"
                    fullWidth
                    label="Title"
                    value={assessment.title ?? ""}
                    onChange={(e) => onChange({ ...assessment, title: e.target.value })}
                />
                <TextField
                    variant="outlined"
                    size="small"
                    fullWidth
                    multiline
                    minRows={2}
                    label="Description"
                    value={assessment.description ?? ""}
                    onChange={(e) => onChange({ ...assessment, description: e.target.value })}
                />

                <div>
                    <Typography variant="caption" color="textSecondary">
                        People at risk
                    </Typography>
                    <div className={classes.chips}>
                        {(assessment.peopleAtRisk ?? []).map((person, i) => (
                            <Chip
                                key={i}
                                label={person}
                                onDelete={() =>
                                    onChange({ ...assessment, peopleAtRisk: assessment.peopleAtRisk.filter((_, n) => n !== i) })
                                }
                            />
                        ))}
                        {!assessment.peopleAtRisk?.length && (
                            <Typography variant="caption" color="textSecondary">
                                None
                            </Typography>
                        )}
                    </div>
                    <div className={classes.addRow}>
                        <TextField
                            variant="outlined"
                            size="small"
                            fullWidth
                            placeholder="Add someone at risk"
                            value={drafts.people ?? ""}
                            onChange={(e) => setDrafts((prev) => ({ ...prev, people: e.target.value }))}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    e.preventDefault();
                                    addPerson();
                                }
                            }}
                        />
                        <Button startIcon={<Add />} onClick={addPerson}>
                            Add
                        </Button>
                    </div>
                </div>
            </Paper>

            <Typography variant="subtitle2">
                {assessment.hazards.length} {assessment.hazards.length === 1 ? "hazard" : "hazards"}
            </Typography>
            <Typography variant="caption" className={classes.scoresNote}>
                Severity and likelihood aren't set here — you score each hazard when you complete the assessment.
            </Typography>

            {assessment.hazards.length === 0 ? (
                <Typography className={classes.empty}>
                    Every hazard has been removed. Add one back or go back to the chat.
                </Typography>
            ) : (
                assessment.hazards.map((hazard, index) => (
                    <Paper key={index} className={classes.hazard}>
                        <div className={classes.hazardHeader}>
                            <div className={classes.hazardFields}>
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    label="Hazard"
                                    value={hazard.name ?? ""}
                                    onChange={(e) => updateHazard(index, { name: e.target.value })}
                                />
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    multiline
                                    minRows={2}
                                    label="How harm occurs"
                                    value={hazard.description ?? ""}
                                    onChange={(e) => updateHazard(index, { description: e.target.value })}
                                />
                            </div>
                            <Tooltip title="Remove hazard">
                                <IconButton
                                    size="small"
                                    aria-label={`Remove ${hazard.name}`}
                                    onClick={() => setHazards(assessment.hazards.filter((_, i) => i !== index))}
                                >
                                    <Delete fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </div>

                        {(Object.keys(listLabels) as HazardListKey[]).map((key) => (
                            <div key={key} className={classes.listBlock}>
                                <Typography variant="caption" color="textSecondary">
                                    {listLabels[key]}
                                </Typography>
                                <div className={classes.chips}>
                                    {hazard[key].map((item, i) => (
                                        <Chip key={i} label={item} onDelete={() => removeItem(index, key, i)} />
                                    ))}
                                    {hazard[key].length === 0 && (
                                        <Typography variant="caption" color="textSecondary">
                                            None
                                        </Typography>
                                    )}
                                </div>
                                <div className={classes.addRow}>
                                    <TextField
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        placeholder={`Add to ${listLabels[key].toLowerCase()}`}
                                        value={drafts[`${index}-${key}`] ?? ""}
                                        onChange={(e) => setDrafts((prev) => ({ ...prev, [`${index}-${key}`]: e.target.value }))}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                                e.preventDefault();
                                                addItem(index, key);
                                            }
                                        }}
                                    />
                                    <Button startIcon={<Add />} onClick={() => addItem(index, key)}>
                                        Add
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </Paper>
                ))
            )}

            <div className={classes.footer}>
                <Button variant="outlined" color="primary" startIcon={<ArrowBack />} onClick={onBack}>
                    Back to chat
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    disabled={isSaving || assessment.hazards.length === 0 || !assessment.title?.trim()}
                    onClick={onCreate}
                >
                    Create template
                </Button>
            </div>
        </div>
    );
}
